/**
 * taxonomy.operations.ts — the TAXONOMY document registry (CU-1b, fullsuite
 * plan §B2.3'/§B2.7'; extends the B2.6'/CU-1a masters seam pattern).
 *
 * The document TEXTS are single-sourced from the contract pack
 * (`packages/contracts/operations/*.graphql`) via vite `?raw` imports, so the
 * facade can never drift from the reviewed contract text. Every document in
 * this registry is machine-validated against the FROZEN SDL snapshot
 * (`packages/contracts/schema.graphql`) by `scripts/validate-operations.mjs`
 * — the gate CU-1b added after finding three B0.7' pricing pages that had
 * never been validated and cited roots the graph never grew.
 *
 * INTERIM (masters precedent; removal trigger: taxonomy adoption in
 * codegen.ts): the hand-declared TYPES below are not yet generated
 * `TypedDocumentNode` re-exports. They ride plain full-text POSTs
 * (graphql.client.ts null-hash rule). The projections mirror the operation
 * fragments field-for-field; drift is guarded by the taxonomySeam vitest.
 *
 * ID CANON (settled BEFORE this slice, unlike CU-1a): the taxonomy graph
 * exposes the RAW category id (`cat-<code>`, `t.exposeID('id')`) — there is
 * no opaque global id to decode, so the CU-1a detail-404 class of defect
 * cannot recur on this surface.
 */

import productCategoryFlatListText from '../../../packages/contracts/operations/ProductCategoryFlatList.graphql?raw';
import productCategoryDetailText from '../../../packages/contracts/operations/ProductCategoryDetail.graphql?raw';
import productCategoryCreateText from '../../../packages/contracts/operations/ProductCategoryCreate.graphql?raw';
import productCategoryUpdateText from '../../../packages/contracts/operations/ProductCategoryUpdate.graphql?raw';
import productCategoryReparentText from '../../../packages/contracts/operations/ProductCategoryReparent.graphql?raw';
import productCategoryDeactivateText from '../../../packages/contracts/operations/ProductCategoryDeactivate.graphql?raw';
import productTaxonomyLevelsText from '../../../packages/contracts/operations/ProductTaxonomyLevels.graphql?raw';
import productTaxonomyLevelUpdateText from '../../../packages/contracts/operations/ProductTaxonomyLevelUpdate.graphql?raw';
import productTaxonomyAuditFeedText from '../../../packages/contracts/operations/ProductTaxonomyAuditFeed.graphql?raw';

/** Operation names — must equal each file's basename (safelist identity). */
export const TAXONOMY_OPERATION_NAMES = {
  categoryFlatList: 'ProductCategoryFlatList',
  categoryDetail: 'ProductCategoryDetail',
  categoryCreate: 'ProductCategoryCreate',
  categoryUpdate: 'ProductCategoryUpdate',
  categoryReparent: 'ProductCategoryReparent',
  categoryDeactivate: 'ProductCategoryDeactivate',
  levels: 'ProductTaxonomyLevels',
  levelUpdate: 'ProductTaxonomyLevelUpdate',
  auditFeed: 'ProductTaxonomyAuditFeed',
} as const;

export const taxonomyOperations = {
  categoryFlatList: productCategoryFlatListText,
  categoryDetail: productCategoryDetailText,
  categoryCreate: productCategoryCreateText,
  categoryUpdate: productCategoryUpdateText,
  categoryReparent: productCategoryReparentText,
  categoryDeactivate: productCategoryDeactivateText,
  levels: productTaxonomyLevelsText,
  levelUpdate: productTaxonomyLevelUpdateText,
  auditFeed: productTaxonomyAuditFeedText,
} as const;

// ---------------------------------------------------------------------------
// Graph shapes — mirror the operation fragments field-for-field
// ---------------------------------------------------------------------------

export interface GqlCategoryExternalMapping {
  system: string;
  code: string;
}

/** The `TaxonomyNodeFields` fragment, plus the nested `children` the tree read adds. */
export interface GqlTaxonomyNode {
  id: string;
  code: string;
  parentId: string | null;
  depth: number;
  path: string;
  sortOrder: number;
  labelEn: string;
  labelZh: string;
  isActive: boolean;
  netsuiteSegmentValueId: string | null;
  description: string | null;
  iconKey: string | null;
  synonyms: string[];
  externalMappings: GqlCategoryExternalMapping[];
  children?: GqlTaxonomyNode[];
}

export interface GqlTaxonomyLevelConfig {
  depth: number;
  enabled: boolean;
  labelEn: string;
  labelZh: string;
}

export interface GqlTaxonomyAuditEntry {
  id: string;
  action: string;
  targetId: string;
  comment: string;
  actorId: string | null;
  at: string;
}

export interface GqlUserError {
  code: string;
  field: string | null;
  message: string;
}

export interface CategoryFlatListResult {
  productTaxonomyTree: GqlTaxonomyNode[];
  productTaxonomyLevelConfigs: GqlTaxonomyLevelConfig[];
}

export interface CategoryFlatListVariables {
  includeInactive?: boolean;
  [key: string]: unknown;
}

export interface CategoryDetailResult {
  productTaxonomyNode: GqlTaxonomyNode | null;
}

export interface CategoryWriteResult {
  productCategoryCreate?: { category: GqlTaxonomyNode | null; userErrors: GqlUserError[] };
  productCategoryUpdate?: { category: GqlTaxonomyNode | null; userErrors: GqlUserError[] };
  productCategoryReparent?: { category: GqlTaxonomyNode | null; userErrors: GqlUserError[] };
}

export interface CategoryDeactivateResult {
  productCategoryDeactivate: {
    category: GqlTaxonomyNode | null;
    reassignedProducts: number;
    userErrors: GqlUserError[];
  };
}

export interface TaxonomyLevelsResult {
  productTaxonomyLevelConfigs: GqlTaxonomyLevelConfig[];
}

export interface TaxonomyLevelUpdateResult {
  productTaxonomyLevelUpdate: {
    level: GqlTaxonomyLevelConfig | null;
    userErrors: GqlUserError[];
  };
}

export interface TaxonomyAuditResult {
  productTaxonomyAudit: GqlTaxonomyAuditEntry[];
}
